import React, { useEffect, useMemo, useState, type ComponentType } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  MdLock as MdLockIcon,
  MdError as MdErrorIcon,
  MdVisibility as MdVisibilityIcon,
  MdVisibilityOff as MdVisibilityOffIcon,
  MdCheckCircle as MdCheckCircleIcon,
  MdAdminPanelSettings as MdAdminPanelSettingsIcon,
} from 'react-icons/md';
import { cn } from '../lib/utils';
import { isPasswordStrong, PASSWORD_POLICY_MESSAGE } from '@euroasiann/shared';
import { EUROASIANN_GROUP_NAME, EUROASIANN_LOGO_DARK, EUROASIANN_TAGLINE } from '../constants/euroasiann-branding';
import mapBackground from '../assets/proper_map_bg.png';

type IconProps = { className?: string };
const MdLock = MdLockIcon as ComponentType<IconProps>;
const MdError = MdErrorIcon as ComponentType<IconProps>;
const MdVisibility = MdVisibilityIcon as ComponentType<IconProps>;
const MdVisibilityOff = MdVisibilityOffIcon as ComponentType<IconProps>;
const MdCheckCircle = MdCheckCircleIcon as ComponentType<IconProps>;
const MdAdminPanelSettings = MdAdminPanelSettingsIcon as ComponentType<IconProps>;

const API_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV ? '' : 'http://localhost:3000');

type LocationState = {
  email?: string;
};

export default function SetInitialPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state || {}) as LocationState;

  const email = useMemo(() => {
    const fromState = state.email?.trim().toLowerCase();
    if (fromState) return fromState;
    return (sessionStorage.getItem('adminForceChangePasswordEmail') || '').trim().toLowerCase();
  }, [state.email]);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!email) {
      setError('Please sign in with your temporary password first.');
    }
  }, [email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Please sign in with your temporary password first.');
      return;
    }
    if (!isPasswordStrong(newPassword)) {
      setError(PASSWORD_POLICY_MESSAGE);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const url =
        import.meta.env.DEV && !import.meta.env.VITE_API_URL
          ? '/api/v1/auth/set-initial-password'
          : `${API_URL}/api/v1/auth/set-initial-password`;

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          newPassword,
          portal: 'admin',
          portalType: 'admin',
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to save password');
      }

      sessionStorage.removeItem('adminForceChangePasswordEmail');
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to save password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-[#060d18] font-sans text-white overflow-hidden">
      <header className="relative z-30 flex justify-between items-center px-6 py-3 border-b border-white/5 backdrop-blur-md bg-[#060d18]/60 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
            <img src={EUROASIANN_LOGO_DARK} alt={EUROASIANN_GROUP_NAME} className="w-full h-full object-contain" />
          </div>
          <div className="flex flex-col">
            <h1 className="font-bold text-sm leading-tight tracking-wide text-white">{EUROASIANN_GROUP_NAME}</h1>
            <p className="text-[9px] uppercase tracking-[0.2em] text-white/50 capitalize">{EUROASIANN_TAGLINE}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-white/70">
          <MdAdminPanelSettings className="text-white/50" />
          <span className="font-medium">Create Password</span>
        </div>
      </header>

      <main className="relative z-20 flex-1 flex overflow-hidden m-3 rounded-2xl border border-white/[0.06]">
        <div className="hidden lg:flex lg:w-[55%] relative bg-[#0a1929] overflow-hidden">
          <img
            src={mapBackground}
            alt="Maritime Map"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#060d18]/70 via-transparent to-[#060d18]/80 z-10" />
          <div className="relative z-20 flex flex-col justify-end h-full p-10 pb-12">
            <h2 className="text-4xl font-bold text-white leading-tight tracking-tight">
              Secure Your<br />
              <span className="text-[#0088cc]">Admin Account</span>
            </h2>
            <p className="text-sm text-white/60 leading-relaxed max-w-md mt-4">
              Your temporary Ship Span password must be replaced before you can access the admin dashboard.
            </p>
          </div>
        </div>

        <div className="w-full lg:w-[45%] flex items-center justify-center p-6 lg:p-10 bg-[#0b1523] relative overflow-y-auto">
          <div className="relative z-10 w-full max-w-sm">
            {success ? (
              <div className="text-center space-y-4">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/30">
                  <MdCheckCircle className="h-8 w-8 text-emerald-400" />
                </div>
                <h2 className="text-2xl font-bold text-white">Password Saved</h2>
                <p className="text-sm text-white/60">
                  Redirecting to login. Sign in with your new password.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-white tracking-tight">Create New Password</h2>
                  <p className="text-sm text-white/60 mt-1.5">
                    {email
                      ? `Set a permanent password for ${email}`
                      : 'Complete first-time setup for your admin account'}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm">
                      <MdError className="w-4 h-4 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-white/70 uppercase tracking-wider">
                      New Password
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <MdLock className="text-white/40 text-lg group-focus-within:text-[#0088cc] transition-colors" />
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={8}
                        className="w-full pl-11 pr-11 py-3 bg-white/[0.06] border border-white/15 rounded-xl text-sm text-white placeholder:text-white/30 outline-none focus:border-[#0088cc]/60 focus:bg-white/[0.08] focus:ring-2 focus:ring-[#0088cc]/15 transition-all tracking-wider"
                        placeholder="Min 8 characters"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-white/40 hover:text-white/70 transition-colors"
                      >
                        {showPassword ? <MdVisibilityOff className="text-lg" /> : <MdVisibility className="text-lg" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-white/70 uppercase tracking-wider">
                      Confirm Password
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <MdLock className="text-white/40 text-lg group-focus-within:text-[#0088cc] transition-colors" />
                      </div>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength={8}
                        className="w-full pl-11 pr-11 py-3 bg-white/[0.06] border border-white/15 rounded-xl text-sm text-white placeholder:text-white/30 outline-none focus:border-[#0088cc]/60 focus:bg-white/[0.08] focus:ring-2 focus:ring-[#0088cc]/15 transition-all tracking-wider"
                        placeholder="Re-enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-white/40 hover:text-white/70 transition-colors"
                      >
                        {showConfirmPassword ? (
                          <MdVisibilityOff className="text-lg" />
                        ) : (
                          <MdVisibility className="text-lg" />
                        )}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !email}
                    className={cn(
                      'w-full py-3 rounded-xl text-sm font-semibold transition-all bg-[#0088cc] hover:bg-[#0099e6] text-white shadow-lg shadow-[#0088cc]/20 active:scale-[0.98]',
                      (loading || !email) && 'opacity-70 cursor-not-allowed'
                    )}
                  >
                    {loading ? 'Saving...' : 'Save Password'}
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate('/login', { replace: true })}
                    className="w-full text-xs text-white/50 hover:text-[#0088cc] transition-colors"
                  >
                    Back to login
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
